import { Stats } from '../sim/types'

export interface CharacterDef {
  id: string
  name: string
  blurb: string
  color: number
  startWeapon: string
  /** Tweak the base stat block for this character's identity (applied once, at the start of recompute). */
  applyBase(stats: Stats): void
  /** Optional per-LEVEL scaling passive (Megabonk-style "grows into its identity"): applied in recomputeStats after
   *  applyBase, scaled by the player's current level. Deterministic — no RNG. Re-evaluated on every level-up. */
  perLevel?(stats: Stats, level: number): void
  /** Optional runtime passive id, read by the sim hot paths (combat.ts / fireWeapons). */
  passive?: 'vampire' | 'berserker' | 'glasscannon' | 'juggernaut' | 'executioner' | 'marksman'
  /** Optional signature gimmick wired into the sim (see updateGimmick / killEnemy / checkLevelUp). */
  gimmick?: 'shockwave' | 'aura' | 'orbit' | 'volley' | 'nova' | 'ricochet' | 'repair' | 'mortar' | 'dice' | 'forage'
  /** Human-readable passive/gimmick description for the character-select screen. */
  passiveDesc?: string
  /** Unlock requirement description; `defaultUnlocked` ones start available. */
  defaultUnlocked: boolean
  unlockHint: string
}

export const CHARACTER_LIST: CharacterDef[] = [
  {
    id: 'bonker',
    name: 'Bonker',
    blurb: 'The all-rounder. Reliable bonks, no surprises.',
    color: 0xffcc44,
    startWeapon: 'boomeraxe',
    applyBase: () => {},
    gimmick: 'shockwave',
    passiveDesc: 'Kills have a chance to release a knockback shockwave.',
    defaultUnlocked: true,
    unlockHint: 'Available from the start.',
  },
  {
    id: 'pip',
    name: 'Pip',
    blurb: 'Tiny and quick. Fragile but fast, fires like mad.',
    color: 0x66e0ff,
    startWeapon: 'revolver',
    applyBase: (s) => {
      s.moveSpeed *= 1.18
      s.attackSpeed += 0.2
      s.maxHp -= 25
    },
    gimmick: 'ricochet',
    passiveDesc: 'Periodically fires a burst of ricocheting bolts at nearby foes.',
    defaultUnlocked: true,
    unlockHint: 'Available from the start.',
  },
  {
    id: 'tank',
    name: 'Brick',
    blurb: 'A wall of muscle. Slow, tough, hits hard.',
    color: 0xd2733a,
    startWeapon: 'scattergun',
    applyBase: (s) => {
      s.maxHp += 60
      s.armor += 6
      s.moveSpeed *= 0.88
      s.damage += 0.1
    },
    perLevel: (s, lvl) => {
      s.armor += 0.5 * lvl // grows tougher every level — Brick becomes a true wall late-run
    },
    passiveDesc: 'Grows tougher: +0.5 armor per level.',
    defaultUnlocked: true,
    unlockHint: 'Available from the start.',
  },
  {
    id: 'witch',
    name: 'Hazel',
    blurb: 'Glass cannon mage. Huge damage, paper thin.',
    color: 0xb06cff,
    startWeapon: 'meteor',
    applyBase: (s) => {
      s.damage += 0.3
      s.area *= 1.15
      s.maxHp -= 35
      s.critChance += 0.05
    },
    gimmick: 'nova',
    passiveDesc: 'Periodically unleashes an arcane nova that erupts around you.',
    defaultUnlocked: false,
    unlockHint: 'Reach level 15 in a single run.',
  },
  {
    id: 'frost',
    name: 'Yuki',
    blurb: 'Controls the cold. Slows the horde to a crawl.',
    color: 0x7fe8ff,
    startWeapon: 'frostpulse',
    applyBase: (s) => {
      s.area *= 1.1
      s.maxHp += 10
      s.pickupRange *= 1.2
    },
    gimmick: 'aura',
    passiveDesc: 'A permanent frost aura damages and slows nearby foes.',
    defaultUnlocked: false,
    unlockHint: 'Survive 5 minutes in one run.',
  },
  {
    id: 'gambler',
    name: 'Lady Luck',
    blurb: 'Lives on RNG. Big luck, big crits, big risk.',
    color: 0x4caf50,
    startWeapon: 'zapper',
    applyBase: (s) => {
      s.luck += 2
      s.critChance += 0.1
      s.critMult += 0.3
      s.goldGain *= 1.3
      s.maxHp -= 15
    },
    perLevel: (s, lvl) => {
      s.luck += 0.06 * lvl // fortune compounds as she levels — the RNG snowball gets stronger
    },
    passiveDesc: 'Fortune compounds: +0.06 luck per level.',
    defaultUnlocked: false,
    unlockHint: 'Bank 1000 total gold across all runs.',
  },
  {
    id: 'vampire',
    name: 'Vlad',
    blurb: 'Feeds on the fallen. Heals a little with every kill.',
    color: 0xc23a4a,
    startWeapon: 'rotaura',
    applyBase: (s) => {
      s.lifesteal += 0.03
      s.maxHp += 20
      s.moveSpeed *= 1.05
    },
    passive: 'vampire',
    passiveDesc: 'Heal 1 HP per kill (scales with max HP).',
    defaultUnlocked: false,
    unlockHint: 'Defeat your first boss.',
  },
  {
    id: 'berserker',
    name: 'Ragnar',
    blurb: 'The lower his health, the harder he hits. Lives on the edge.',
    color: 0xd44a2a,
    startWeapon: 'lash',
    applyBase: (s) => {
      s.maxHp += 40
      s.damage += 0.1
      s.armor += 3
    },
    passive: 'berserker',
    passiveDesc: 'Up to +70% damage as your health drops.',
    defaultUnlocked: false,
    unlockHint: 'Reach level 20 in a single run.',
  },
  {
    id: 'engineer',
    name: 'Dot',
    blurb: 'More dakka. Trades raw power for an extra projectile on everything.',
    color: 0x4aa0c2,
    startWeapon: 'caltrops',
    applyBase: (s) => {
      s.projectileCount += 1
      s.area *= 1.08
      s.damage -= 0.1
      s.moveSpeed *= 0.95
    },
    gimmick: 'mortar',
    passiveDesc: 'Periodically lobs an explosive mortar shell onto a nearby enemy.',
    defaultUnlocked: false,
    unlockHint: 'Bank 2500 total gold across all runs.',
  },
  {
    id: 'monk',
    name: 'Kai',
    blurb: 'Fleet-footed brawler. Fast, evasive, relentless.',
    color: 0xe0a040,
    startWeapon: 'sawblade',
    applyBase: (s) => {
      s.moveSpeed *= 1.22
      s.attackSpeed += 0.12
      s.dodge += 0.08
      s.maxHp -= 10
    },
    perLevel: (s, lvl) => {
      s.dodge += 0.004 * lvl // ever more elusive as he levels — leans into the evasion fantasy
    },
    passiveDesc: 'Ever more elusive: +0.4% dodge per level.',
    defaultUnlocked: false,
    unlockHint: 'Survive 8 minutes in one run.',
  },
  {
    id: 'gunner',
    name: 'Tex',
    blurb: 'Bullet hell incarnate. Fires fast and wide, but hits light.',
    color: 0xb0b040,
    startWeapon: 'revolver',
    applyBase: (s) => {
      s.attackSpeed += 0.18
      s.projectileCount += 1
      s.damage -= 0.12
      s.critChance += 0.05
    },
    gimmick: 'volley',
    passiveDesc: 'Periodically unloads a wide volley of bullets all around you.',
    defaultUnlocked: false,
    unlockHint: 'Slay 3000 enemies across all runs.',
  },
  {
    id: 'juggernaut',
    name: 'Atlas',
    blurb: 'A walking fortress of a mech. Slow, immense, soaks punishment.',
    color: 0x9aa6b2,
    startWeapon: 'halo',
    applyBase: (s) => {
      s.maxHp += 90
      s.armor += 8
      s.moveSpeed *= 0.8
      s.knockback += 0.4
    },
    passive: 'juggernaut',
    passiveDesc: 'Heavy plating soaks 30% of all incoming damage.',
    defaultUnlocked: false,
    unlockHint: 'Survive 10 minutes in one run.',
  },
  {
    id: 'glasscannon',
    name: 'Nova',
    blurb: 'A reactor in a spacesuit. Devastating output, near-zero shielding.',
    color: 0xff4d6d,
    startWeapon: 'railgun',
    applyBase: (s) => {
      s.maxHp -= 45
      s.critChance += 0.08
      s.critMult += 0.25
      s.moveSpeed *= 1.05
    },
    passive: 'glasscannon',
    passiveDesc: '+40% damage dealt, but +60% damage taken.',
    defaultUnlocked: false,
    unlockHint: 'Deal 200 in a single hit.',
  },
  {
    id: 'executioner',
    name: 'Reaper',
    blurb: 'Harvester of the dying. Finishes weakened aliens in one strike.',
    color: 0x7b5cc4,
    startWeapon: 'singularity',
    applyBase: (s) => {
      s.damage += 0.12
      s.critChance += 0.04
      s.moveSpeed *= 1.04
      s.maxHp -= 10
    },
    passive: 'executioner',
    passiveDesc: '+75% damage to enemies below 30% HP.',
    defaultUnlocked: false,
    unlockHint: 'Kill 5 bosses across all runs.',
  },
  {
    id: 'swarm',
    name: 'Echo',
    blurb: 'A swarm-control drone unit. Floods the field with projectiles.',
    color: 0x3fd6c0,
    startWeapon: 'drones',
    applyBase: (s) => {
      s.projectileCount += 2
      s.area *= 1.12
      s.duration *= 1.15
      s.damage -= 0.18
      s.moveSpeed *= 0.97
    },
    gimmick: 'orbit',
    passiveDesc: 'Two attack drones permanently orbit you, shredding what they touch.',
    defaultUnlocked: false,
    unlockHint: 'Own 5 weapons at once in a run.',
  },
  {
    id: 'sustain',
    name: 'Pulsar',
    blurb: 'Self-repairing exo-suit. Outlasts everything through attrition.',
    color: 0x6fd06a,
    startWeapon: 'sporecloud',
    applyBase: (s) => {
      s.maxHp += 35
      s.hpRegen += 1.4
      s.lifesteal += 0.02
      s.armor += 3
      s.moveSpeed *= 0.98
    },
    gimmick: 'repair',
    passiveDesc: 'Periodic self-repair pulse: heals and grants a small shield.',
    defaultUnlocked: false,
    unlockHint: 'Bank 5000 total gold across all runs.',
  },
  {
    id: 'marksman',
    name: 'Vesper',
    blurb: 'A dead-eye sniper. Her weapons fire wherever she aims — point and shoot.',
    color: 0x4ad0a0,
    startWeapon: 'railgun',
    applyBase: (s) => {
      s.critChance += 0.1
      s.critMult += 0.3
      s.projectileSpeed *= 1.12
      s.maxHp -= 20
    },
    perLevel: (s, lvl) => {
      s.critChance += 0.004 * lvl // her eye sharpens every level — the crit-precision fantasy compounds
    },
    passive: 'marksman',
    passiveDesc: 'Manual aim: your weapons fire where you point (toward your facing), not just the nearest foe. +crit precision.',
    defaultUnlocked: false,
    unlockHint: 'Land a 500-damage hit in a single run.',
  },
  {
    id: 'cinder',
    name: 'Ember',
    blurb: 'A pyromancer who lives for the burn. Afflicted foes melt under her.',
    color: 0xff6a2d,
    startWeapon: 'solarflare',
    applyBase: (s) => {
      s.statusDamage += 0.25
      s.damage += 0.12
      s.area *= 1.1
      s.maxHp -= 15
    },
    perLevel: (s, lvl) => {
      s.statusDamage += 0.012 * lvl // her flames bite ever deeper into burning/poisoned foes as she levels
    },
    passiveDesc: 'Afflicted foes take ever more: +25% status damage, growing +1.2% per level.',
    defaultUnlocked: false,
    unlockHint: 'Bank 8000 total gold across all runs.',
  },
  {
    id: 'bastion',
    name: 'Bastion',
    blurb: 'A bulwark exo-frame. Hides behind a regenerating energy shield that never quits.',
    color: 0x6aa0e0,
    startWeapon: 'shocknova',
    applyBase: (s) => {
      s.maxShield += 60
      s.armor += 5
      s.maxHp += 30
      s.moveSpeed *= 0.9
    },
    perLevel: (s, lvl) => {
      s.maxShield += 6 * lvl // the shield bank swells every level — a true attrition wall late-run
    },
    passiveDesc: 'Starts with a big regenerating shield: +60 shield, growing +6 per level.',
    defaultUnlocked: false,
    unlockHint: 'Survive 12 minutes in one run.',
  },
  {
    id: 'dicer',
    name: 'Roll',
    blurb: 'A living gamble. Every level-up rolls a random permanent buff — no two runs alike.',
    color: 0xe8d24a,
    startWeapon: 'ricochet',
    applyBase: (s) => {
      s.luck += 1
      s.critChance += 0.04
    },
    gimmick: 'dice',
    passiveDesc: 'Each level-up rolls one of six permanent stat surges (power / fire rate / crit / x2 / HP / armor).',
    defaultUnlocked: false,
    unlockHint: 'Reach level 25 in a single run.',
  },
  {
    id: 'forager',
    name: 'Stash',
    blurb: 'A relentless scavenger. Keeps unearthing free gold while everyone else just fights.',
    color: 0xc8a23a,
    startWeapon: 'caltrops',
    applyBase: (s) => {
      s.goldGain *= 1.4
      s.pickupRange *= 1.3
      s.maxHp += 15
    },
    gimmick: 'forage',
    passiveDesc: 'Periodically unearths a free cache of gold near you.',
    defaultUnlocked: false,
    unlockHint: 'Bank 12000 total gold across all runs.',
  },
]

export const CHARACTERS: Record<string, CharacterDef> = {}
for (const c of CHARACTER_LIST) CHARACTERS[c.id] = c
